/**
 * logger - Utilitário de logging condicional.
 * Logs aparecem apenas em ambiente de desenvolvimento.
 * 
 * Princípio: Segurança - evita exposição de logs sensíveis em produção.
 */

const isDev = import.meta.env?.DEV ?? process.env.NODE_ENV !== 'production';

type LogLevel = 'log' | 'warn' | 'error' | 'debug' | 'info';

interface Logger {
    log: (...args: unknown[]) => void;
    warn: (...args: unknown[]) => void;
    error: (...args: unknown[]) => void;
    debug: (...args: unknown[]) => void;
    info: (...args: unknown[]) => void;
    group: (label: string) => void;
    groupEnd: () => void;
}

const createLogger = (): Logger => {
    const conditionalLog = (level: LogLevel) => (...args: unknown[]) => {
        if (isDev) {
            console[level](...args);
        }
    };

    return {
        log: conditionalLog('log'),
        warn: conditionalLog('warn'),
        error: (...args: unknown[]) => console.error(...args), // Sempre log erros
        debug: conditionalLog('debug'),
        info: conditionalLog('info'),
        group: (label: string) => isDev && console.group(label),
        groupEnd: () => isDev && console.groupEnd()
    };
};

export const logger = createLogger();

/**
 * Logger com prefixo para módulos específicos.
 * @param prefix - Prefixo do módulo (ex: "[ChatSync]")
 */
export const createModuleLogger = (prefix: string): Logger => ({
    log: (...args: unknown[]) => logger.log(prefix, ...args),
    warn: (...args: unknown[]) => logger.warn(prefix, ...args),
    error: (...args: unknown[]) => logger.error(prefix, ...args),
    debug: (...args: unknown[]) => logger.debug(prefix, ...args),
    info: (...args: unknown[]) => logger.info(prefix, ...args),
    group: (label: string) => logger.group(`${prefix} ${label}`),
    groupEnd: () => logger.groupEnd()
});
